import React from 'react';

import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {useAccountSettingsState} from 'app/settings/api';
import {useUpdateUser} from 'app/user/api';
import {gt} from 'app/utils/i18n';

import type {UserSerializedType} from 'sweet-types';

interface Props {
  user: UserSerializedType;
}

export function UserRoleSwitchButton({user}: Props) {
  const [updateUser, {isLoading}] = useUpdateUser();
  const {owner_email} = useAccountSettingsState().data!;

  return user.email === owner_email ? (
    <button className="btn btn-sm btn-primary" disabled>
      {gt('Account Owner')}
    </button>
  ) : (
    <UncontrolledDropdown>
      <DropdownToggle
        caret
        color="primary"
        className="btn-sm"
        disabled={isLoading}
      >
        <span>
          {isLoading ? <i className="fa fa-circle-notch fa-spin mr-1" /> : null}
          {getPermissionName({
            is_super_manager: user.is_super_manager,
            is_super_teammate: user.is_super_teammate,
          })}
        </span>
      </DropdownToggle>
      <DropdownMenu right>
        <DropdownItem
          onClick={() =>
            updateUser({
              id: user.id,
              is_super_teammate: false,
              is_super_manager: false,
            })
          }
          active={!(user.is_super_manager || user.is_super_teammate)}
        >
          <LazyTooltip
            placement="right"
            tooltip={gt('Member can view what they have been assigned.')}
          >
            <span>
              <i
                className={`far ${
                  user.is_super_manager || user.is_super_teammate
                    ? 'fa-square'
                    : 'fa-check-square'
                }`}
              />{' '}
              {getPermissionName({})}
            </span>
          </LazyTooltip>
        </DropdownItem>
        <DropdownItem
          onClick={() =>
            updateUser({
              id: user.id,
              is_super_teammate: false,
              is_super_manager: true,
            })
          }
          active={user.is_super_manager}
        >
          <LazyTooltip
            placement="right"
            tooltip={gt('Member can edit all procedures, processes and teams.')}
          >
            <span>
              <i
                className={`far ${
                  user.is_super_manager ? 'fa-check-square' : 'fa-square'
                }`}
              />{' '}
              {getPermissionName({is_super_manager: true})}
            </span>
          </LazyTooltip>
        </DropdownItem>
        <DropdownItem
          onClick={() =>
            updateUser({
              id: user.id,
              is_super_teammate: true,
              is_super_manager: false,
            })
          }
          active={user.is_super_teammate}
        >
          <LazyTooltip
            placement="right"
            tooltip={gt('Member can view all procedures, processes and teams.')}
          >
            <span>
              <i
                className={`far ${
                  user.is_super_teammate ? 'fa-check-square' : 'fa-square'
                }`}
              />{' '}
              {getPermissionName({is_super_teammate: true})}
            </span>
          </LazyTooltip>
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

function getPermissionName({
  is_super_manager,
  is_super_teammate,
}: {
  is_super_manager?: boolean;
  is_super_teammate?: boolean;
}) {
  if (is_super_manager === true) {
    return (
      <span>
        <i className="far fa-star super-manager-icon" />
        &nbsp;{gt('Super Manager')}
      </span>
    );
  } else if (is_super_teammate === true) {
    return gt('Super Teammate');
  } else {
    return gt('Regular Member');
  }
}
